import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { AoSGameState } from '../../types';
import { computeTotalExpenses } from '../../types';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 7: 費用支払フェーズ
 *
 * 各プレイヤーは経費（株式配当 + 機関車維持費）を自動的に支払います。
 * ロジック処理後、ユーザーがクリックするまで待機します。
 *
 * 処理内容:
 * - 株式配当: sharesIssued × $1
 * - 機関車維持費: LOCOMOTIVE_COSTS[locomotiveLevel]
 * - 合計経費を現金から支払う
 * - 現金不足の場合、収入レベルを減少
 * - 収入レベルが負になった場合、倒産
 * - 倒産プレイヤーは処理をスキップ
 *
 * 参照:
 * - docs/specifications/logic/common/phase_07_pay_expenses.md
 * - docs/specifications/logic/barbados/phase_07_pay_expenses_override.md
 */
export const payExpensesPhase: PhaseConfig<AoSGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[payExpensesPhase] ★★★ Phase 7: Pay Expenses started ★★★');
    debug.log('[payExpensesPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ソロプレイ用: Player 0の経費支払
    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      // 倒産時は即座に次フェーズへ（結果表示なし）
      G.autoPhaseResult = null;
      return;
    }

    // 経費計算（共通関数）
    const totalExpense = computeTotalExpenses(G);

    debug.log(`[payExpensesPhase] Player 0 expenses: $${totalExpense}`);

    // 支払処理
    if (player.cash >= totalExpense) {
      // 現金が十分な場合
      player.cash -= totalExpense;
      debug.log(`[payExpensesPhase] Player 0 paid $${totalExpense}, remaining cash: $${player.cash}`);
    } else {
      // 現金が不足する場合
      const shortage = totalExpense - player.cash;
      player.cash = 0;
      player.incomeLevel -= shortage;

      debug.log(`[payExpensesPhase] Player 0 insufficient cash, shortage: $${shortage}, incomeLevel: ${player.incomeLevel}`);
    }

    // 処理結果を保存（UI表示用）
    G.autoPhaseResult = {
      phase: 'payExpenses',
      message: 'Paying dividends and locomotive costs...',
      floatingText: `-$${totalExpense}`,
      details: {
        expenses: totalExpense,
      },
    };

    debug.log('[payExpensesPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: AoSGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart,
  },

  // 倒産プレイヤーは自動的に次フェーズへ、または Undo 強制遷移
  endIf: ({ G }: { G: AoSGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[payExpensesPhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[payExpensesPhase] ★★★ onEnd called - Phase 7 ended ★★★');
    debug.log('[payExpensesPhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log('[payExpensesPhase] ★★★ Next phase should be: incomeReduction');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('payExpenses');
  },

  // Phase 8 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: AoSGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'incomeReduction';
  }
};
